import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ScrollArea } from "@/components/ui/scroll-area";
import { podcastService } from "@/lib/podcastService";
import { useAudioPlayer } from "@/contexts/AudioPlayerContext";
import QueueItem from "./AudioPlayer/QueueItem";
import ShareDialog from "./ui/share-dialog";
import { Button } from "./ui/button";
import { LoadingState } from "./ui/loading-state";
import { ErrorState } from "./ui/error-state";
import { useAsync } from "@/hooks/use-async";
import { useToast } from "./ui/use-toast";
import { Heart } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/lib/supabase";

const PodcastPage = () => {
  const { id } = useParams();
  const { data: podcast, loading, error, execute } = useAsync();
  const [subscriberCount, setSubscriberCount] = useState(0);
  const [showShareDialog, setShowShareDialog] = React.useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const { currentEpisode, playEpisode } = useAudioPlayer();
  const { user } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();

  useEffect(() => {
    if (id) {
      execute(() => podcastService.getPodcast(id));
    }
  }, [id, execute]);

  useEffect(() => {
    if (podcast) {
      setSubscriberCount(podcast.subscriber_count || 0);
    }
  }, [podcast]);

  // Check if user is subscribed when component mounts
  useEffect(() => {
    const checkSubscription = async () => {
      if (!user || !id) return;
      try {
        const isUserSubscribed = await podcastService.getSubscriptionStatus(
          user.id,
          id,
        );
        setIsSubscribed(isUserSubscribed);
      } catch (error) {
        console.error("Error checking subscription:", error);
        setIsSubscribed(false);
      }
    };

    checkSubscription();
  }, [user, id]);

  const handleSubscribe = async () => {
    if (!user) {
      navigate("/login");
      return;
    }

    if (!id) return;

    try {
      const result = await podcastService.togglePodcastSubscription(
        user.id,
        id,
      );

      if (result.success) {
        setIsSubscribed(result.subscribed);
        // Update subscriber count
        const newCount = await podcastService.getSubscriberCount(id);
        setSubscriberCount(newCount);

        toast({
          title: result.subscribed ? "Subscribed!" : "Unsubscribed",
          description: result.subscribed
            ? "You'll now receive updates for this podcast"
            : "You'll no longer receive updates for this podcast",
        });
      }
    } catch (error) {
      console.error("Error toggling subscription:", error);
      toast({
        title: "Error",
        description: "Failed to update subscription",
        variant: "destructive",
      });
    }
  };

  if (loading) {
    return (
      <div className="w-full bg-white/20 backdrop-blur-md rounded-2xl border border-white/20 shadow-xl overflow-hidden p-6">
        <LoadingState label="Loading podcast..." />
      </div>
    );
  }

  if (error || !podcast) {
    return (
      <div className="w-full bg-white/20 backdrop-blur-md rounded-2xl border border-white/20 shadow-xl overflow-hidden p-6">
        <ErrorState
          title="Failed to load podcast"
          error={error}
          onRetry={() => id && execute(() => podcastService.getPodcast(id))}
        />
      </div>
    );
  }

  return (
    <div
      className="w-full bg-white/20 backdrop-blur-md rounded-2xl border border-white/20 shadow-xl overflow-hidden flex flex-col h-full"
      role="region"
      aria-label={`${podcast.title} podcast page`}
    >
      <div className="flex-none p-4">
        <div className="flex items-center gap-3">
          <img
            src={podcast.imageUrl}
            alt={`${podcast.title} cover art`}
            className="w-[56px] h-[56px] rounded-lg object-cover flex-shrink-0"
          />
          <div className="flex-1 min-w-0">
            <h1 className="text-base font-semibold truncate">
              {podcast.title}
            </h1>
            <p className="text-sm text-gray-600">
              {subscriberCount.toLocaleString()} subscribers •{" "}
              {podcast.episodes.length} clips
            </p>
          </div>
          <Button
            variant="outline"
            size="icon"
            className="flex-shrink-0 h-10 w-10 bg-white/10 hover:bg-white/20 border-white/20"
            onClick={handleSubscribe}
          >
            <Heart
              className={`h-5 w-5 stroke-[1.5px] ${isSubscribed ? "fill-[#735fe2] text-[#735fe2]" : ""}`}
            />
          </Button>
        </div>
      </div>

      <div className="flex-1 overflow-hidden">
        <ScrollArea className="h-full">
          <div
            className="divide-y divide-white/5"
            role="list"
            aria-label="Episodes list"
          >
            {podcast.episodes.map((episode) => {
              // Map the episode data to include required fields for tracking
              const mappedEpisode = {
                ...episode,
                podcastId: podcast.id,
                podcastName: podcast.title,
              };

              return (
                <QueueItem
                  key={episode.id}
                  title={episode.title}
                  duration={episode.duration}
                  imageUrl={episode.imageUrl}
                  listens={episode.listen_count}
                  likes={episode.like_count}
                  isActive={episode.id === currentEpisode?.id}
                  onClick={() => playEpisode(mappedEpisode, false)}
                />
              );
            })}
          </div>
        </ScrollArea>
      </div>

      {showShareDialog && currentEpisode && (
        <ShareDialog
          title={currentEpisode.title}
          url={currentEpisode.audioUrl}
          open={showShareDialog}
          onOpenChange={setShowShareDialog}
        />
      )}
    </div>
  );
};

export default PodcastPage;
